import React from 'react';
import cn from 'classnames';

import type { MediaAttachment } from 'widget/types/attachments';
import IconButton from 'widget/shared/ui/IconButton';
import { WidgetTexts } from 'widget/consts/texts';
import ChevronLeftIcon from 'widget/shared/icons/ChevronLeftIcon';
import { IconSize } from 'widget/shared/icons/BaseIcon';

import useMediaViewer from '../hooks/useMediaViewer';
import useControlsVisibility from '../hooks/useControlsVisibility';
import useSwipe from '../hooks/useSwipe';

import MediaViewerItem from './MediaViewerItem';
import MediaViewerToolbar from './MediaViewerToolbar';
import styles from './MediaViewerBody.module.scss';

type MediaViewerBodyProps = {
  media: MediaAttachment[];
  initialIndex: number;
  onClose: () => void;
  onError?: (error: Error) => void;
};

const MediaViewerBody: React.FC<MediaViewerBodyProps> = ({
  media,
  initialIndex,
  onClose,
  onError
}) => {
  const {
    currentIndex,
    currentMedia,
    displayedMedia,
    hasMultiple,
    isMediaVisible,
    handleGoToPrev,
    handleGoToNext,
    handleTransitionEnd,
    handleDownload,
    handleOverlayClick,
    overlayRef
  } = useMediaViewer({ media, initialIndex, onClose, onError });

  const { controlsOpacity, handleUserActivity } = useControlsVisibility();

  useSwipe({
    onSwipeLeft: handleGoToNext,
    onSwipeRight: handleGoToPrev,
    ref: overlayRef,
    disabled: !hasMultiple
  });

  if (!currentMedia) {
    return null;
  }

  const controlsStyle = { opacity: controlsOpacity };

  return (
    <div
      ref={overlayRef}
      className={styles['overlay']}
      onClick={handleOverlayClick}
      onMouseMove={handleUserActivity}
      onTouchStart={handleUserActivity}
    >
      <MediaViewerToolbar
        onDownloadMedia={handleDownload}
        onCloseMediaViewer={onClose}
        controlsStyle={controlsStyle}
      />
      {hasMultiple && (
        <IconButton
          className={cn(styles['overlay__nav'], styles['overlay__nav_prev'])}
          onClick={handleGoToPrev}
          aria-label={WidgetTexts.mediaViewer.previous.label}
          style={controlsStyle}
        >
          <ChevronLeftIcon size={IconSize.L} />
        </IconButton>
      )}
      <MediaViewerItem
        media={displayedMedia}
        isHidden={!isMediaVisible}
        onTransitionEnd={handleTransitionEnd}
        className={styles['overlay__media-item']}
      />
      {hasMultiple && (
        <IconButton
          className={cn(styles['overlay__nav'], styles['overlay__nav_next'])}
          onClick={handleGoToNext}
          aria-label={WidgetTexts.mediaViewer.next.label}
          style={controlsStyle}
        >
          <ChevronLeftIcon
            className={styles['overlay__nav-icon_next']}
            size={IconSize.L}
          />
        </IconButton>
      )}
      {hasMultiple && (
        <div className={styles['overlay__media-counter']} style={controlsStyle}>
          {currentIndex + 1} / {media.length}
        </div>
      )}
    </div>
  );
};

export default MediaViewerBody;
